import React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/core';

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

const SkeletonContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 16px;
  padding-top: 16px;
  font-family: 'Nunito Sans', sans-serif;
  color: #444444;
  background: #FCFCFC;
  box-sizing: border-box;
  position: relative;
  flex: 1;
  min-height: 0;
  gap: 8px;
`;

const SkeletonSurface = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: #ffffff;
  overflow: visible;
  flex: 1;
  min-height: 0;
  position: relative;
  border: 1px solid #EEEEEE;
  border-radius: 0;
`;

const SkeletonScrollContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  -webkit-overflow-scrolling: touch;
  position: relative;
  padding-left: 4px;
  margin-left: -4px;
`;

const SkeletonTimelineContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
`;

const SkeletonTableHeader = styled.div`
  display: flex;
  width: 100%;
  position: sticky;
  top: 0;
  z-index: 30;
  background: #FFFFFF;
`;

const SkeletonTableBody = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
`;

const SkeletonWeekHeaderCell = styled.div`
  width: 210px;
  min-width: 210px;
  padding: 8px;
  background: #FFFFFF;
  border-right: 1px solid #EEEEEE;
  border-bottom: 1px solid #EEEEEE;
  display: flex;
  gap: 8px;
  align-items: center;
`;

const SkeletonBlocksHeaderCell = styled.div`
  flex: 1;
  padding: 8px;
  background: #FFFFFF;
  border-right: 1px solid #EEEEEE;
  border-bottom: 1px solid #EEEEEE;
  display: flex;
  align-items: center;
`;

const SkeletonNotesHeaderCell = styled.div`
  width: 300px;
  min-width: 300px;
  padding: 8px;
  background: #FFFFFF;
  border-bottom: 1px solid #EEEEEE;
  display: flex;
  align-items: center;
`;

const SkeletonHeaderText = styled.div`
  width: ${(props) => props.width || '60px'};
  height: 16px;
  background: rgba(15, 23, 42, 0.08);
  border-radius: 4px;
  animation: ${pulse} 1.5s ease-in-out infinite;
`;

const SkeletonTableRow = styled.div`
  display: flex;
  width: 100%;
  min-height: 56px;
  position: relative;
`;

const SkeletonWeekCell = styled.div`
  width: 210px;
  min-width: 210px;
  padding: 8px;
  background: #FFFFFF;
  border-right: 1px solid #EEEEEE;
  border-left: 1px solid #EEEEEE;
  border-top: 1px solid #EEEEEE;
  border-bottom: ${(props) => (props.isLast ? '1px solid #EEEEEE' : '0')};
  display: flex;
  gap: 16px;
  align-items: center;
`;

const SkeletonWeekLabel = styled.div`
  width: ${(props) => props.width || '50px'};
  height: 16px;
  background: rgba(15, 23, 42, 0.08);
  border-radius: 4px;
  animation: ${pulse} 1.5s ease-in-out infinite;
`;

const SkeletonBlocksCell = styled.div`
  flex: 1;
  min-height: 56px;
  padding: 4px;
  background: #FFFFFF;
  border-right: 1px solid #EEEEEE;
  border-top: 1px solid #EEEEEE;
  border-bottom: ${(props) => (props.isLast ? '1px solid #EEEEEE' : '0')};
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const SkeletonBlock = styled.div`
  width: ${(props) => props.width || '120px'};
  height: 48px;
  background: rgba(15, 23, 42, 0.08);
  border-radius: 4px;
  animation: ${pulse} 1.5s ease-in-out infinite;
  animation-delay: ${(props) => props.delay || '0s'};
`;

const SkeletonNotesCell = styled.div`
  width: 300px;
  min-width: 300px;
  min-height: 56px;
  padding: 4px;
  background: #FFFFFF;
  border-top: 1px solid #EEEEEE;
  border-right: 1px solid #EEEEEE;
  border-bottom: ${(props) => (props.isLast ? '1px solid #EEEEEE' : '0')};
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
`;

const SkeletonNoteText = styled.div`
  width: ${(props) => props.width || '100%'};
  height: 12px;
  background: rgba(15, 23, 42, 0.08);
  border-radius: 4px;
  animation: ${pulse} 1.5s ease-in-out infinite;
  animation-delay: ${(props) => props.delay || '0s'};
`;

const ReviewViewSkeleton = () => {
  const weeks = Array.from({ length: 8 }, (_, i) => i);

  const blockPatterns = [
    [{ width: '120px' }, { width: '160px' }],
    [{ width: '140px' }],
    [{ width: '100px' }, { width: '130px' }, { width: '110px' }],
    [{ width: '180px' }],
    [{ width: '150px' }, { width: '120px' }],
    [],
    [{ width: '130px' }, { width: '140px' }],
    [{ width: '110px' }],
  ].map((pattern, patternIndex) => pattern.map((block, blockIndex) => ({
    ...block,
    id: `block-${patternIndex}-${blockIndex}`,
  })));

  const notePatterns = [
    [{ width: '90%' }, { width: '70%' }],
    [{ width: '80%' }],
    [],
    [{ width: '95%' }, { width: '85%' }, { width: '60%' }],
    [{ width: '75%' }],
    [{ width: '88%' }, { width: '92%' }],
    [],
    [{ width: '70%' }, { width: '80%' }],
  ].map((pattern, patternIndex) => pattern.map((note, noteIndex) => ({
    ...note,
    id: `note-${patternIndex}-${noteIndex}`,
  })));

  return (
    <SkeletonContainer>
      <SkeletonSurface>
        <SkeletonScrollContainer>
          <SkeletonTimelineContainer>
            <SkeletonTableHeader>
              <SkeletonWeekHeaderCell>
                <SkeletonHeaderText width='40px' />
              </SkeletonWeekHeaderCell>
              <SkeletonBlocksHeaderCell>
                <SkeletonHeaderText width='50px' />
              </SkeletonBlocksHeaderCell>
              <SkeletonNotesHeaderCell>
                <SkeletonHeaderText width='45px' />
              </SkeletonNotesHeaderCell>
            </SkeletonTableHeader>

            <SkeletonTableBody>
              {weeks.map((week, index) => {
                const blocks = blockPatterns[week % blockPatterns.length];
                const notes = notePatterns[week % notePatterns.length];
                const isLast = index === weeks.length - 1;

                return (
                  <SkeletonTableRow key={week}>
                    <SkeletonWeekCell isLast={isLast}>
                      <SkeletonWeekLabel width='40px' />
                      <SkeletonWeekLabel width='110px' />
                    </SkeletonWeekCell>

                    <SkeletonBlocksCell isLast={isLast}>
                      {blocks.length === 0 ? (
                        <SkeletonBlock width='140px' />
                      ) : (
                        blocks.map((block) => (
                          <SkeletonBlock
                            key={block.id}
                            width={block.width}
                            delay={`${blocks.indexOf(block) * 0.1}s`}
                          />
                        ))
                      )}
                    </SkeletonBlocksCell>

                    <SkeletonNotesCell isLast={isLast}>
                      {notes.length === 0 ? (
                        <SkeletonNoteText width='60%' />
                      ) : (
                        notes.map((note) => (
                          <SkeletonNoteText
                            key={note.id}
                            width={note.width}
                            delay={`${notes.indexOf(note) * 0.1}s`}
                          />
                        ))
                      )}
                    </SkeletonNotesCell>
                  </SkeletonTableRow>
                );
              })}
            </SkeletonTableBody>
          </SkeletonTimelineContainer>
        </SkeletonScrollContainer>
      </SkeletonSurface>
    </SkeletonContainer>
  );
};

export default ReviewViewSkeleton;
